import { useEffect } from 'react';
import {
  INote,
  IGenerateRequest,
  IResponseChecklist,
} from '../global/interfaces';
import {
  IDelta,
  IChecklist,
  IDocumentParagraph,
  IDocumentInput,
} from '@homeporter/types';

import { getChunks, deleteAINotes } from '../utils/helpers';

interface UseProcessChecklistProps {
  booking: any; // TODO: Add proper type
  documentSection: any; // TODO: Add proper type
  context: string;
  transcriptSegments: any[]; // TODO: Add proper type
  transcriptAttachments: Record<string, any[]>; // TODO: Add proper type
  setGenerateRequest: (request: any) => void; // TODO: Add proper type
  setBookingContext: (booking: any, context: string) => void;
  setSaveEnabled: (enabled: boolean) => void;
  regenerateIndex: number | undefined;
  setRegenerateIndex: (index: number | undefined) => void;
  responseChecklist: IResponseChecklist | null;
  setDocumentSection: (section: any) => void;
  setTranscriptSegments: (segments: any[]) => void;
  setScrollTo: (scrollTo: string | undefined) => void;
  resetAttachments: (
    bookingId: number | undefined,
    transcriptAttachments: Record<string, (IDelta | INote)[]>,
  ) => void;
  generateRequest: IGenerateRequest | undefined;
}

export const useProcessChecklist = ({
  booking,
  documentSection,
  context,
  transcriptSegments,
  transcriptAttachments,
  setGenerateRequest,
  setBookingContext,
  setSaveEnabled,
  regenerateIndex,
  responseChecklist,
  setDocumentSection,
  setTranscriptSegments,
  setScrollTo,
  resetAttachments,
  generateRequest,
}: UseProcessChecklistProps) => {
  useEffect(() => {
    if (
      !booking?.booking_id ||
      regenerateIndex === undefined ||
      !documentSection?.cards[regenerateIndex]
    ) {
      setGenerateRequest(undefined);
      return;
    }
    if (documentSection.cards[regenerateIndex].type !== 'Checklist') return;
    console.log(':::::regenerateIndex checklist card:', regenerateIndex);

    const checklistCard = documentSection.cards[regenerateIndex] as IChecklist;
    const _context =
      checklistCard.context || documentSection.context || context;

    if (!documentSection.context) documentSection.context = _context;
    if (!checklistCard.context) checklistCard.context = _context;
    setBookingContext(booking, _context);

    const validatedCard = documentSection.cards.some(
      (card: any) => card.type === 'Checklist' && card.validated,
    );
    if (!validatedCard) {
      deleteAINotes(transcriptAttachments);
      setTranscriptSegments([...transcriptSegments]);
    }

    const criteriaList: string[] = [];
    checklistCard.data.forEach((sectionEntry) => {
      const sectionName = sectionEntry.title;

      sectionEntry.children.forEach((child) => {
        const categoryName = child.subtitle;
        const items = child.items;

        for (const item of items) {
          item.air = undefined;
          item.uir = undefined;
          item.asids = undefined;
          item.usids = undefined;
          criteriaList.push(
            `        ${item.idx}. ${sectionName}, ${categoryName}, ${item.label}`,
          );
        }
      });
    });

    console.log(':::::criteriaList:', criteriaList);

    const sorted = criteriaList //confirm that the idx is always in the correct order, should be fine now that we're using arrays
      .sort((a, b) => {
        const numA = parseInt(a.split('.')[0], 10);
        const numB = parseInt(b.split('.')[0], 10);
        return numA - numB;
      })
      .join('\n');

    setGenerateRequest({
      bookingId: booking.booking_id,
      regenerateIndex,
      algorithm: checklistCard.algorithm.toLowerCase().replaceAll(' ', '-'),
      type: 'Checklist',
      chunks: getChunks(transcriptSegments, checklistCard.min_word_count),
      index: 0,
      criteria: sorted, //criteriaList.join('\n'),
      context: _context,
      temperature: checklistCard.temperature,
    });
  }, [regenerateIndex]);

  useEffect(() => {
    if (
      !responseChecklist ||
      !documentSection ||
      regenerateIndex === undefined ||
      generateRequest?.chunks === undefined ||
      generateRequest.index === undefined
    )
      return;

    const rawCriteria = responseChecklist.checklistCriteria;
    const criteriaSet = new Set(
      Array.isArray(rawCriteria)
        ? rawCriteria.map(Number).filter((n) => !isNaN(n))
        : typeof rawCriteria === 'number'
          ? [rawCriteria]
          : typeof rawCriteria === 'string'
            ? rawCriteria
                .split(/[\s,]+/)
                .map(Number)
                .filter((n) => !isNaN(n))
            : [],
    );

    const checklistCard = documentSection.cards[
      generateRequest.regenerateIndex
    ] as IChecklist;

    const currentChunk = generateRequest.chunks?.[generateRequest.index];

    checklistCard.data.forEach((sectionEntry) => {
      sectionEntry.children.forEach((child) => {
        child.items.forEach((item: IDocumentInput) => {
          if (criteriaSet.has(item.idx)) {
            if (currentChunk?.sids) {
              item.asids = [...(item.asids || []), ...currentChunk?.sids];
            }

            item.air = '1';
            console.log(':::::item update air:', item);
          }
        });
      });
    });

    console.log('::::setDocument section 3...');
    setDocumentSection((prev: any) =>
      prev
        ? {
            ...prev,
            cards: prev.cards.map((card: any, idx: number) =>
              idx === regenerateIndex ? { ...checklistCard } : card,
            ),
          }
        : null,
    );
    setSaveEnabled(true);

    (transcriptAttachments[`note-${currentChunk.timestamp}`] ??= []).push({
      title: `Algorithm: ${generateRequest.algorithm}, ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
      description: `Summary: ${responseChecklist.cleanedTranscript}\nContext: ${responseChecklist.context}`,
      idx: transcriptAttachments[`note-${currentChunk.timestamp}`].length,
      src: 'a',
    });

    setTranscriptSegments([...transcriptSegments]);
    setScrollTo(`note-${currentChunk.timestamp}`);

    //if (generateRequest.index < 3) {
    if (generateRequest.index < generateRequest.chunks?.length - 1 || 0) {
      console.log(':::::generateRequest.index:', generateRequest.index);

      setGenerateRequest({
        ...generateRequest,
        index: generateRequest.index + 1,
        context: responseChecklist.context,
      });
    } else {
      resetAttachments(booking?.booking_id, transcriptAttachments);
      setScrollTo(undefined);
      setGenerateRequest(undefined);
    }
  }, [responseChecklist]);
};
